import { autoUpdate, flip, offset, type Placement, useClick, useFloating, useInteractions } from '@floating-ui/react';
import classNames from 'classnames';
import { FocusTrap } from 'focus-trap-react'; // If FocusTrap is used for other "full-screen" UIs, consider splitting the overlay + FocusTrap into its own component
import { cloneElement, type JSX, useMemo, useRef } from 'react';
import useAnimateInOutComponent from '@alltrails/core/hooks/useAnimateInOutComponent';
import useGroupHover from '@alltrails/core/hooks/useGroupHover';
import useOffClick from '@alltrails/core/hooks/useOffClick';
import { SPACE_4 } from '../../tokens';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import styles from './styles/styles.module.scss';

type PopoverProps = {
  anchor: JSX.Element;
  children: React.ReactNode;
  className?: string;
  containerMouseHandlers?: {
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
  };
  'data-denali'?: string;
  placement?: Extract<Placement, 'bottom-start' | 'bottom-end' | 'bottom' | 'top-start' | 'top-end'>;
  isOpen?: boolean;
  onCloseRequest: () => void;
  returnFocusOnDeactivate?: boolean;
  isMobile?: boolean;
};

const Popover = ({
  anchor,
  children,
  className,
  containerMouseHandlers,
  'data-denali': dataDenali,
  placement = 'bottom-start',
  isOpen = false,
  onCloseRequest,
  isMobile = false
}: PopoverProps) => {
  const { isAnimating, isVisible, onAnimationEnd } = useAnimateInOutComponent(isOpen);
  const containerRef = useRef<HTMLDivElement>(null);

  const { refs, floatingStyles, context } = useFloating({
    placement,
    middleware: [flip(), offset({ mainAxis: SPACE_4, crossAxis: 0 })],
    whileElementsMounted: autoUpdate
  });

  const click = useClick(context);

  const { getFloatingProps } = useInteractions([click]);

  useOffClick({ engaged: isOpen, callback: onCloseRequest, container: containerRef });

  const anchorEl = useMemo(() => cloneElement(anchor, { ref: refs.setReference }), [anchor, refs]);

  // useGroupHover, along with a little css, is needed to avoid an issue where hovering in between the anchor and the popover can cause
  // the popover to repeatedly open and close since onMouseEnter is triggered as the popover animates out.
  const { onMouseEnter, onMouseLeave } = useGroupHover(containerMouseHandlers?.onMouseEnter, containerMouseHandlers?.onMouseLeave);

  return (
    <div ref={containerRef} {...setDenaliAttribute(dataDenali || 'Popover')} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      {anchorEl}
      {(isAnimating || isVisible || isMobile) && (
        <FocusTrap
          active={isVisible}
          // don't use clickOutsideDeactivates because it can lead to weird issues with clicking on the anchor while the popover is open
          // fallbackFocus is used to prevent errors in testing https://github.com/focus-trap/focus-trap-react/issues/91
          focusTrapOptions={{
            allowOutsideClick: true,
            clickOutsideDeactivates: false,
            escapeDeactivates: true,
            onDeactivate: onCloseRequest,
            fallbackFocus: '#popover',
            initialFocus: false
          }}
        >
          <div
            ref={refs.setFloating}
            {...getFloatingProps()}
            className={classNames(
              styles.popper,
              styles[placement.split('-')[0]],
              {
                [styles.isAnimating]: isAnimating,
                [styles.isVisible]: isVisible
              },
              className
            )}
            id="popover"
            onAnimationEnd={onAnimationEnd}
            style={floatingStyles}
          >
            {children}
          </div>
        </FocusTrap>
      )}
    </div>
  );
};

export default Popover;
